import { evaluateClientQuota, getThreadTitle } from '@frontend/api-client';
import type {
  ImageKitAuthentication,
  LocalFile,
  RetrieveThreadResponse,
  ThreadMessage,
  ThreadReferenceInput,
  ThreadTaskSuggestion,
  ThreadTaskSuggestionMessage,
} from '@frontend/api-types';
import { P, last, match, strToObjectJson } from '@frontend/duck-tape';
import { z } from '@frontend/forms';
import {
  convertImageKitFilesToThreadAttachments,
  getImageKitImageUrl,
  uploadFilesToImageKit,
  useEffect,
  useRef,
  useState,
  useVisibilityController,
} from '@frontend/react';
import type { ThreadInputMessageState } from '@frontend/react-domain';
import { useThreadChat, useThreadLinks } from '@frontend/react-domain';
import { useAuthSlice } from '@frontend/redux';
import type { FlatListProps } from '@frontend/web-react';
import {
  AnimatingFloatingButton,
  Banner,
  FlatList,
  Header,
  LoadingSpinnerPanel,
  PageContainer,
  YStack,
  useScrollStatus,
  useScrollView,
} from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import { ChatFooter, ThreadChatWhisper, ThreadSuggestionMessage, ThreadTextMessage } from '@frontend/web/components';
import { ThreadLinksModal } from '@frontend/web/components/DomainSpecific/Threads/ThreadLinksModal';
import type { ThreadMarkdownRendererProps } from '@frontend/web/components/DomainSpecific/Threads/ThreadMarkdownRenderer';
import {
  useLazyAuthenticateImageKitQuery,
  useLazyRetrieveThreadEntityLinksQuery,
  useLazyRetrieveThreadQuery,
  useRetrieveClientStatusQuery,
} from '@frontend/web/hooks';
import { useCreateTaskContext } from '@frontend/web/hooks/context/useCreateTaskContext';
import { createFileRoute } from '@frontend/web/utils';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils/sockets';

export type ThreadReferenceForTask = {
  id: NonNullable<ThreadReferenceInput['id']>;
  messageIdx: NonNullable<ThreadReferenceInput['messageIdx']>;
  taskSuggestionIdx: NonNullable<ThreadReferenceInput['taskSuggestionIdx']>;
} & { suggestion: ThreadTaskSuggestion };

const Thread = () => {
  const { threadId } = Route.useParams();
  const { autoMessageSendContent = '', files = '' } = Route.useSearch();
  const { token } = useAuthSlice();
  const navigate = Route.useNavigate();
  const [retrieveThreadData, { isLoading: isThreadLoading }] = useLazyRetrieveThreadQuery();
  const [authenticateImageKit] = useLazyAuthenticateImageKitQuery();
  const [hasSentInitialMessage, setHasSentInitialMessage] = useState(false);
  const { scrollRef, scrollToBottom } = useScrollView({ onRenderScroll: 'bottom' });
  const { isScrolled } = useScrollStatus(scrollRef);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const [inputMessage, setInputMessage] = useState<ThreadInputMessageState>({
    current: '',
    // Refers to message that was used to generate the current AI response, needed for "Retry" logic on error
    inFlight: '',
  });
  const [threadFiles, setThreadFiles] = useState<LocalFile[]>([]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const { openCreateTaskModal } = useCreateTaskContext();

  // Thread entities
  const [retrieveThreadEntityLinks] = useLazyRetrieveThreadEntityLinksQuery();
  const [isThreadLinksModalOpen, { close: closeThreadLinksModal, open: openThreadLinksModal }] =
    useVisibilityController();

  const { isThreadEntityLinksPollingLoading, linksData, queryThreadEntityLinks, setLinksData } = useThreadLinks({
    retrieveThreadEntityLinks: async (uuid, useCache) => (await retrieveThreadEntityLinks(uuid, useCache)).data,
  });

  const [threadData, setThreadData] = useState<RetrieveThreadResponse>();

  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const isThrottled = Boolean(clientStatus && evaluateClientQuota(clientStatus).isThrottled);

  const getAndSetThreadData = async () => {
    const { data } = await retrieveThreadData(threadId);
    setThreadData(data);
  };

  const handleImageKit = async (files: LocalFile[]) => {
    setIsSubmitLoading(true);
    const handleAuthenticateImageKit = async (): Promise<ImageKitAuthentication | undefined> => {
      const { data } = await authenticateImageKit();
      return data;
    };
    const imageKitFiles = await uploadFilesToImageKit(files, handleAuthenticateImageKit);
    setIsSubmitLoading(false);
    return convertImageKitFilesToThreadAttachments(imageKitFiles);
  };

  const onClickScrollDownButton = () => scrollToBottom();

  const { interruptStream, isAIStreaming, isStreamingError, messages, sendThreadMessage, threadRequestType, title } =
    useThreadChat({
      convertFilesToThreadAttachments: handleImageKit,
      getImageKitImageUrl: getImageKitImageUrl,
      onBlurTextInput: () => textInputRef.current?.blur(),
      refetchThread: getAndSetThreadData,
      scrollToBottom,
      setInputMessage,
      setThreadFiles,
      socket: {
        disconnectSocket,
        getSocket,
        initializeSocket,
      },
      threadData,
      threadId,
      token,
    });

  // Opens the create task slideup when the first suggestion is received in the creator flow
  useEffect(() => {
    if (threadRequestType === 'create' && messages.length === 3 && !isAIStreaming) {
      const suggestionMessage = messages?.[0] as ThreadTaskSuggestionMessage;
      const suggestionContent = suggestionMessage.content;
      const initialMessage = last(messages) as ThreadMessage;

      if (suggestionContent.length) {
        const suggestion = suggestionContent[0]!;

        openCreateTaskModal({
          initialData: {
            description: initialMessage.content as string,
            name: suggestion.title,
            researchThreadId: threadId,
            researchThreadReferenceData: {
              id: threadId,
              // Length of messages is 3
              messageIdx: 2,
              suggestion,
              // Only ever one suggestion in the creator flow
              taskSuggestionIdx: 0,
            },
          },
          isRecurrence: suggestion.isRecurrence,
          onCreateCallback: getAndSetThreadData,
          stepsToSkip: ['description'],
        });
      }
    }
  }, [threadRequestType, messages, isAIStreaming]);

  useEffect(() => {
    getAndSetThreadData();
  }, [threadId]);

  useEffect(() => {
    if (autoMessageSendContent && !hasSentInitialMessage && threadData) {
      sendThreadMessage(autoMessageSendContent, files ? strToObjectJson<LocalFile[]>(files) : [], { threadId });
      setHasSentInitialMessage(true);
      navigate({ params: { threadId }, to: '/app/thread/$threadId' });
    }
  }, [hasSentInitialMessage, threadData]);

  useEffect(() => {
    return () => {
      setHasSentInitialMessage(false);
    };
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const onClickTaskSuggestion = (researchThreadReferenceData: ThreadReferenceForTask) =>
    match(researchThreadReferenceData.suggestion)
      .with({ recurrenceId: P.string }, ({ recurrenceId }) =>
        navigate({
          params: { recurrenceId },
          to: '/app/recurrence/$recurrenceId',
        }),
      )
      .with({ taskId: P.string }, ({ taskId }) =>
        navigate({
          params: researchThreadReferenceData.suggestion.isDraftTask ? { draftTaskId: taskId } : { taskId },
          to: researchThreadReferenceData.suggestion.isDraftTask ? '/app/draft-task/$draftTaskId' : '/app/task/$taskId',
        }),
      )
      .otherwise(() => {
        const initialMessage = last(messages) as ThreadMessage;
        const useInitialMessageAsDescription = messages.length === 3 && threadRequestType === 'create';

        openCreateTaskModal({
          initialData: {
            description: useInitialMessageAsDescription
              ? (initialMessage.content as string)
              : researchThreadReferenceData.suggestion.title,
            name: researchThreadReferenceData.suggestion.title,
            researchThreadId: threadId,
            researchThreadReferenceData: researchThreadReferenceData,
          },
          isRecurrence: researchThreadReferenceData.suggestion.isRecurrence,
          onCreateCallback: getAndSetThreadData,
          stepsToSkip: ['description'],
        });
      });

  const onClickEntity: ThreadMarkdownRendererProps['onClickLink'] = async (linkData) => {
    openThreadLinksModal();
    queryThreadEntityLinks(linkData);
  };

  const renderItem: FlatListProps<ThreadMessage>['renderItem'] = ({ array, index, item: message }) => {
    const messageIsStreaming = index === 0 && isAIStreaming;
    const reversedIndex = array.length - 1 - index;
    const ContentComponent = match(message)
      .with({ role: 'ai_recommender', type: 'task_suggestions' }, (message) => (
        <ThreadSuggestionMessage
          isStreaming={messageIsStreaming}
          isThrottled={isThrottled}
          message={message}
          messageIndex={reversedIndex}
          onClickSuggestionCard={onClickTaskSuggestion}
          researchThreadId={threadId}
          threadRequestType={threadRequestType || threadData?.initialRequestType}
        />
      ))
      .with({ role: 'ai_bot', type: 'task_created' }, (message) => (
        <ThreadChatWhisper
          content={{
            onClickBoldText: () => {
              navigate({ params: { taskId: message.content.id }, to: '/tasks/$taskId' });
            },
            taskTitle: message.content.name,
          }}
        />
      ))
      .with({ role: 'ai_bot', type: 'draft_task_created' }, (message) => (
        <ThreadChatWhisper
          content={{
            onClickBoldText: () => {
              navigate({ params: { draftTaskId: message.content.id }, to: '/app/draft-task/$draftTaskId' });
            },
            taskTitle: message.content.name,
          }}
        />
      ))
      .with({ role: 'ai_bot', type: 'recurrence_created' }, (message) => {
        return (
          <ThreadChatWhisper
            content={{
              onClickBoldText: () =>
                navigate({
                  params: { recurrenceId: message.content.id },
                  to: '/app/recurrence/$recurrenceId',
                }),
              taskTitle: message.content.name,
            }}
          />
        );
      })
      .with({ role: P.union('ai_bot', 'client') }, (message) => (
        <ThreadTextMessage
          index={index}
          isStreaming={messageIsStreaming}
          message={message}
          messagesList={messages}
          onClickLink={onClickEntity}
          threadId={threadId}
        />
      ))
      .exhaustive();

    return (
      <YStack gapY="md">
        {ContentComponent}
        {isStreamingError && index === 0 ? (
          <Banner
            color="danger"
            leftIconName="IconRefresh"
            onClick={() => sendThreadMessage(inputMessage.inFlight, [], { threadId })}
            title={'Oops! We hit a snag generating your response. Retry?'}
          />
        ) : null}
      </YStack>
    );
  };

  return (
    <PageContainer className="flex-1" isScrollable={false} padding="none">
      <YStack className="flex-1 overflow-y-auto">
        {/* Inverting FlatList and reversing comments so that most recent messages are laid out first */}
        {isThreadLoading ? (
          <LoadingSpinnerPanel fullScreen />
        ) : (
          <FlatList
            ListFooterComponent={
              <YStack className="bottom-none bg-trueWhite sticky">
                {isScrolled ? (
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  <AnimatingFloatingButton className="top-[-38px]" onClick={onClickScrollDownButton} />
                ) : null}
                <ChatFooter
                  className="w-full max-w-screen-md self-center"
                  filesValue={threadFiles}
                  isStreamResponseLoading={isAIStreaming}
                  isSubmitLoading={isSubmitLoading}
                  onChangeFiles={(files) => setThreadFiles(files)}
                  onChangeText={(messageValue) => {
                    setInputMessage((cur) => ({ ...cur, current: messageValue }));
                  }}
                  onClickInterrupt={interruptStream}
                  onClickSubmit={() => sendThreadMessage(inputMessage.current, threadFiles, { threadId })}
                  placeholder="Describe your task..."
                  submitDisabled={!inputMessage.current && !isAIStreaming}
                  submitShortcut="Enter"
                  textInputDisabled={isSubmitLoading || isAIStreaming}
                  textValue={inputMessage.current}
                />
              </YStack>
            }
            ListHeaderComponent={
              <Header
                className="bg-trueWhite top-none sticky"
                onClickRightIcon={() => navigate({ to: '/app/thread/create' })}
                rightIconName="IconPencil"
                title={isThreadLoading ? '' : getThreadTitle(threadData ? { ...threadData, title } : undefined)}
              />
            }
            className="px-md pb-md flex-1"
            contentContainerClassName={safeCss('py-md gap-y-xl max-w-screen-md self-center w-full justify-end flex-1')}
            data={messages}
            inverted
            ref={scrollRef}
            renderItem={({ index, item }) => renderItem({ array: messages, index, item })}
          />
        )}
        <ThreadLinksModal
          isOpen={isThreadLinksModalOpen}
          isRetrieveLinksLoading={isThreadEntityLinksPollingLoading}
          linkText={linksData?.selectedText}
          onClose={() => {
            closeThreadLinksModal();
            setLinksData({ selectedText: undefined, siteData: [], status: undefined });
          }}
          siteData={linksData.siteData}
          status={linksData?.status}
          threadId={threadId}
        />
      </YStack>
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/thread/$threadId')({
  component: Thread,
  shouldReload: true,
  validateSearch: (search) =>
    z
      .object({
        autoMessageSendContent: z.string().optional(),
        files: z.string().optional(),
      })
      .parse(search),
});
